import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Button, Card, Col, Form, Row, Table, Spinner, Badge, Modal,
} from "react-bootstrap";
import { FaPlus, FaCheck, FaBan, FaPrint, FaImage, FaPen, FaInfoCircle, FaLink } from "react-icons/fa";
import { buildChips, countActiveChips, formatDateChip, resolveMapLabel } from "./utils/accountingFilterUtils";
import AccountingFilterSection from "./components/AccountingFilterSection";
import AccountingActiveFilters from "./components/AccountingActiveFilters";
import "./accountingFilters.css";
import "./accountingResponsive.css";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import axiosInstance from "../../axiosInstance";
import { API_URL_All } from "../../api";
import AdLayout from "../../components/AdLayout";
import { Pagination } from "../../components/ui";
import { openVoucherPrintWindow, fmtDate } from "./utils/printVoucher";
import { formatCategoryLabel } from "./utils/categoryLabel";
import { formatVND as fmtVND, toMoneyNumber } from "./utils/moneyFormat";
import MoneyInput from "./components/MoneyInput";
import { fetchAccountingPermissions, ACCOUNTING_VOUCHER_RECEIPT } from "../../libs/customerCreatePermission";

const PAGE_SIZE = 20;
const DESC_TRUNCATE_LEN = 40;

const STATUS_MAP = {
  DRAFT: { label: "Nháp", bg: "secondary" },
  APPROVED: { label: "Đã duyệt", bg: "info" },
  CONFIRMED: { label: "Đã xác nhận", bg: "success" },
  CANCELLED: { label: "Đã huỷ", bg: "danger" },
};

const parseEvidencePaths = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
};

const resolveUploadUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${API_URL_All}${path}`;
};

const cardStyle = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" };
const unwrap = (res) => res?.data?.data ?? res?.data ?? {};

const INITIAL_FORM = {
  payerId: "",
  amount: "",
  bankAccountId: "",
  lineIds: [],
  categoryId: "",
  description: "",
};

const toLineIdList = (row) => {
  if (Array.isArray(row?.lineIds) && row.lineIds.length) {
    return row.lineIds.map((id) => String(id));
  }
  if (row?.lineId) return [String(row.lineId)];
  return [];
};

const flattenCategories = (nodes, out = []) => {
  (nodes || []).forEach((node) => {
    out.push(node);
    if (node.children?.length) flattenCategories(node.children, out);
  });
  return out;
};

const EMPTY_RECEIPT_FILTERS = {
  customerCode: "",
  keyword: "",
  status: "",
  bankAccountId: "",
  receiptType: "",
  dateFrom: "",
  dateTo: "",
};

const RECEIPT_TYPE_OPTIONS = [
  { value: "AUTO_RECONCILIATION", label: "[AUTO] Thu tiền đối soát đơn hàng khách hàng" },
  { value: "RECEIVABLE", label: "[MAN] Thu công nợ khách hàng" },
  { value: "OTHER", label: "[MAN] Thu nhập thủ công" },
];

const ReceiptVoucherList = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  const [payerObjects, setPayerObjects] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [lines, setLines] = useState([]);
  const [categories, setCategories] = useState([]);

  const [filterCustomerCode, setFilterCustomerCode] = useState("");
  const [filterKeyword, setFilterKeyword] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBankAccount, setFilterBankAccount] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterReceiptType, setFilterReceiptType] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_RECEIPT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [evidenceModal, setEvidenceModal] = useState(null);
  const [descriptionModal, setDescriptionModal] = useState(null);
  const [allocationModal, setAllocationModal] = useState(null);
  const [perm, setPerm] = useState({ canCreate: false, canEdit: false, canDelete: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchAccountingPermissions(axiosInstance, ACCOUNTING_VOUCHER_RECEIPT);
      if (!cancelled) {
        setPerm({
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const payerOptions = useMemo(
    () => payerObjects.map((o) => ({
      value: o.id,
      label: `${o.code || ""} — ${o.name || ""}`.trim(),
      categoryId: o.categoryId ? String(o.categoryId) : "",
    })),
    [payerObjects],
  );

  const selectedPayer = useMemo(
    () => payerOptions.find((o) => o.value === Number(form.payerId)) || null,
    [payerOptions, form.payerId],
  );

  const chipDefs = useMemo(() => [
    { key: "keyword", label: "Mã phiếu", isActive: (v) => Boolean(v) },
    { key: "customerCode", label: "Mã KH", isActive: (v) => Boolean(v) },
    {
      key: "bankAccountId",
      label: "TK nhận",
      isActive: (v) => Boolean(v),
      formatValue: (v) => {
        const b = bankAccounts.find((a) => String(a.id) === String(v));
        return b ? `${b.bankName} - ${b.accountNumber}` : v;
      },
    },
    {
      key: "status",
      label: "Trạng thái",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveMapLabel(v, STATUS_MAP),
    },
    {
      key: "receiptType",
      label: "Loại thu",
      isActive: (v) => Boolean(v),
      formatValue: (v) => RECEIPT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v,
    },
    {
      key: "dateRange",
      label: "Thời gian",
      isActive: (_, applied) => Boolean(applied.dateFrom || applied.dateTo),
      formatValue: (_, applied) => formatDateChip(applied.dateFrom, applied.dateTo),
    },
  ], [bankAccounts]);

  const activeChips = useMemo(
    () => buildChips(appliedFilters, chipDefs),
    [appliedFilters, chipDefs]
  );

  const fetchDropdowns = useCallback(async () => {
    try {
      const [pRes, bRes, lRes, catRes] = await Promise.all([
        axiosInstance.get("/api/accounting/revenue-objects/active").catch(() => null),
        axiosInstance.get("/api/accounting/bank-accounts/active").catch(() => null),
        axiosInstance.get("/api/accounting/receipt-vouchers/lines").catch(() => null),
        axiosInstance.get("/api/accounting/revenue-categories/tree").catch(() => null),
      ]);
      const payerData = unwrap(pRes);
      const bankData = unwrap(bRes);
      const lineData = unwrap(lRes);
      if (payerData) setPayerObjects(Array.isArray(payerData) ? payerData : []);
      if (bankData) {
        const accs = Array.isArray(bankData) ? bankData : bankData?.content || [];
        setBankAccounts(accs);
      }
      if (lineData) setLines(Array.isArray(lineData) ? lineData : []);
      const catData = unwrap(catRes);
      if (catData) setCategories(flattenCategories(Array.isArray(catData) ? catData : []));
    } catch (_) { /* silent */ }
  }, []);

  const fetchData = useCallback(async (p = 0, filters = appliedFilters) => {
    setLoading(true);
    try {
      const params = { page: p, size: PAGE_SIZE };
      if (filters.customerCode) params.customerCode = filters.customerCode;
      if (filters.keyword) params.keyword = filters.keyword;
      if (filters.status) params.status = filters.status;
      if (filters.bankAccountId) params.bankAccountId = filters.bankAccountId;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.receiptType) params.type = filters.receiptType;

      const res = await axiosInstance.get("/api/accounting/receipt-vouchers", { params });
      const body = unwrap(res);
      setRows(body.content || []);
      setTotalPages(body.totalPages || 0);
      setTotalElements(body.totalElements || 0);
      setPage(p);
    } catch (err) {
      toast.error("Không thể tải danh sách phiếu thu");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchDropdowns();
    fetchData(0, EMPTY_RECEIPT_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (next) => {
    setAppliedFilters(next);
    fetchData(0, next);
  };

  const syncDraftFromApplied = (filters) => {
    setFilterCustomerCode(filters.customerCode || "");
    setFilterKeyword(filters.keyword || "");
    setFilterStatus(filters.status || "");
    setFilterBankAccount(filters.bankAccountId || "");
    setFilterReceiptType(filters.receiptType || "");
    setFilterFrom(filters.dateFrom || "");
    setFilterTo(filters.dateTo || "");
  };

  const handleSearch = () => {
    const next = {
      customerCode: filterCustomerCode.trim(),
      keyword: filterKeyword.trim(),
      status: filterStatus,
      bankAccountId: filterBankAccount,
      receiptType: filterReceiptType,
      dateFrom: filterFrom,
      dateTo: filterTo,
    };
    applyFilters(next);
  };

  const handleReset = () => {
    syncDraftFromApplied(EMPTY_RECEIPT_FILTERS);
    applyFilters(EMPTY_RECEIPT_FILTERS);
  };

  const handleRemoveChip = (key) => {
    const base = { ...appliedFilters };
    if (key === "dateRange") {
      base.dateFrom = "";
      base.dateTo = "";
    } else {
      base[key] = "";
    }
    syncDraftFromApplied(base);
    applyFilters(base);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm(INITIAL_FORM);
  };

  const openCreate = () => {
    setEditId(null);
    setForm(INITIAL_FORM);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      payerId: row.payerId ? String(row.payerId) : "",
      amount: row.amount != null ? String(row.amount) : "",
      bankAccountId: row.bankAccountId ? String(row.bankAccountId) : "",
      lineIds: toLineIdList(row),
      categoryId: row.categoryId ? String(row.categoryId) : "",
      description: row.description || "",
    });
    setShowModal(true);
  };

  const onFormChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const lineOptions = useMemo(
    () => lines.map((l) => ({ value: String(l.id), label: l.name || l.lineName || `#${l.id}` })),
    [lines]
  );

  const selectedLines = useMemo(
    () => lineOptions.filter((o) => form.lineIds.includes(o.value)),
    [lineOptions, form.lineIds]
  );

  const buildPayload = () => {
    const lineIds = (form.lineIds || []).map((id) => Number(id)).filter((id) => !Number.isNaN(id));
    return {
      payerId: form.payerId ? Number(form.payerId) : null,
      amount: toMoneyNumber(form.amount),
      bankAccountId: form.bankAccountId ? Number(form.bankAccountId) : null,
      lineIds,
      lineId: lineIds.length ? lineIds[0] : null,
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      description: form.description,
      receiptType: "OTHER",
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.payerId || !form.amount || !form.bankAccountId || !form.categoryId) {
      toast.warn("Vui lòng điền đầy đủ thông tin bắt buộc (kể cả danh mục thu)");
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const res = editId
        ? await axiosInstance.put(`/api/accounting/receipt-vouchers/${editId}`, payload)
        : await axiosInstance.post("/api/accounting/receipt-vouchers", payload);
      if (res?.data?.success === false) {
        toast.error(res.data.message || (editId ? "Cập nhật phiếu thu thất bại" : "Tạo phiếu thu thất bại"));
        return;
      }
      toast.success(editId ? "Cập nhật phiếu thu thành công" : "Tạo phiếu thu thành công");
      closeModal();
      fetchData(page);
    } catch (err) {
      toast.error(err.response?.data?.message || (editId ? "Cập nhật phiếu thu thất bại" : "Tạo phiếu thu thất bại"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    const result = await Swal.fire({
      title: "Xác nhận đúng, không chỉnh sửa gì thêm?",
      text: "Sau khi duyệt, phiếu sẽ khóa chỉnh sửa và phản ánh công nợ cho khách hàng.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Duyệt",
      cancelButtonText: "Huỷ",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.put(`/api/accounting/receipt-vouchers/${id}/approve`);
      if (res?.data?.success === false) {
        toast.error(res.data.message || "Duyệt phiếu thất bại");
        return;
      }
      toast.success("Đã duyệt phiếu thu");
      fetchData(page);
    } catch (err) {
      toast.error(err.response?.data?.message || "Duyệt phiếu thất bại");
    }
  };

  const handleConfirm = async (id, receiptType) => {
    const isManual = receiptType !== "AUTO_RECONCILIATION";

    if (isManual) {
      const step = await Swal.fire({
        title: "Xác nhận đã thu và phản ánh vào dòng tiền?",
        text: "Công nợ khách hàng sẽ được tất toán sau khi xác nhận.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Xác nhận",
        cancelButtonText: "Huỷ",
      });
      if (!step.isConfirmed) return;
    } else {
      const result = await Swal.fire({
        title: "Xác nhận phiếu thu?",
        text: "Phiếu thu sau khi xác nhận sẽ không thể chỉnh sửa.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Xác nhận",
        cancelButtonText: "Huỷ",
      });
      if (!result.isConfirmed) return;
    }

    try {
      const res = await axiosInstance.put(`/api/accounting/receipt-vouchers/${id}/confirm`);
      if (res?.data?.success === false) {
        toast.error(res.data.message || "Xác nhận thất bại");
        return;
      }
      toast.success("Đã xác nhận phiếu thu");
      fetchData(page);
    } catch (err) {
      toast.error(err.response?.data?.message || "Xác nhận thất bại");
    }
  };

  const handleCancel = async (id) => {
    const result = await Swal.fire({
      title: "Huỷ phiếu thu?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Huỷ phiếu",
      cancelButtonText: "Đóng",
      confirmButtonColor: "#dc3545",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.put(`/api/accounting/receipt-vouchers/${id}/cancel`);
      if (res?.data?.success === false) {
        toast.error(res.data.message || "Huỷ phiếu thất bại");
        return;
      }
      toast.success("Đã huỷ phiếu thu");
      fetchData(page);
    } catch (err) {
      toast.error(err.response?.data?.message || "Huỷ phiếu thất bại");
    }
  };

  const handlePrint = async (id) => {
    try {
      const res = await axiosInstance.get(`/api/accounting/receipt-vouchers/${id}`);
      const v = unwrap(res);
      openVoucherPrintWindow("PHIẾU THU", [
        ["Mã phiếu", v.code],
        ["Ngày thu", fmtDate(v.receiptDate)],
        ["Đối tượng thu", v.payerCode || v.payerName || (v.payerId ? `#${v.payerId}` : "—")],
        ["Line KD", v.lineName || "—"],
        ["Danh mục", v.categoryDisplayLabel || formatCategoryLabel(v.receiptType, v.categoryName, true)],
        ["Mô tả", v.description],
      ], v.amount, STATUS_MAP[v.status]?.label || v.status);
    } catch (_) {
      toast.error("Không thể in phiếu thu");
    }
  };

  const handleViewAllocations = async (id) => {
    try {
      const res = await axiosInstance.get(`/api/accounting/receipt-vouchers/${id}/allocations`);
      const rows = unwrap(res);
      setAllocationModal({ id, rows: Array.isArray(rows) ? rows : [] });
    } catch (_) {
      toast.error("Không thể tải phân bổ công nợ");
    }
  };

  const renderDescription = (text) => {
    if (!text) return "—";
    if (text.length <= DESC_TRUNCATE_LEN) return text;
    return (
      <span className="d-inline-flex align-items-center gap-1">
        {text.slice(0, DESC_TRUNCATE_LEN)}…
        <FaInfoCircle
          className="text-primary flex-shrink-0"
          style={{ cursor: "pointer" }}
          title="Xem đầy đủ"
          onClick={() => setDescriptionModal(text)}
        />
      </span>
    );
  };


  const body = (
      <div className={embedded ? "" : "acc-page"}>
        <Card className="mb-3 acc-filter-card">
          <Card.Body className="p-0">
            <AccountingFilterSection
              title="Phiếu Thu"
              open={filterOpen}
              onToggle={setFilterOpen}
              onSubmit={handleSearch}
              onReset={handleReset}
              activeCount={countActiveChips(activeChips)}
              toolbarRight={perm.canCreate && (
                <Button variant="primary" size="sm" onClick={openCreate}>
                  <FaPlus className="me-1" /> Tạo phiếu thu
                </Button>
              )}
            >
              <Row className="g-2 align-items-end acc-filter-grid">
                <Col>
                  <Form.Label className="small mb-1">Mã phiếu</Form.Label>
                  <Form.Control size="sm" value={filterKeyword} onChange={(e) => setFilterKeyword(e.target.value)} placeholder="PT-... hoặc ID" />
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Mã KH</Form.Label>
                  <Form.Control
                    size="sm"
                    value={filterCustomerCode}
                    onChange={(e) => setFilterCustomerCode(e.target.value)}
                    placeholder="Nhập mã KH..."
                  />
                </Col>
                <Col>
                  <Form.Label className="small mb-1">TK nhận</Form.Label>
                  <Form.Select size="sm" value={filterBankAccount} onChange={(e) => setFilterBankAccount(e.target.value)}>
                    <option value="">Tất cả</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>{b.bankName} - {b.accountNumber}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Trạng thái</Form.Label>
                  <Form.Select size="sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">Tất cả</option>
                    {Object.entries(STATUS_MAP).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Loại thu</Form.Label>
                  <Form.Select size="sm" value={filterReceiptType} onChange={(e) => setFilterReceiptType(e.target.value)}>
                    <option value="">Tất cả</option>
                    {RECEIPT_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Từ ngày</Form.Label>
                  <Form.Control type="date" size="sm" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Đến ngày</Form.Label>
                  <Form.Control type="date" size="sm" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
                </Col>
              </Row>
            </AccountingFilterSection>
            <AccountingActiveFilters
              chips={activeChips}
              onRemove={handleRemoveChip}
              onClearAll={handleReset}
            />
          </Card.Body>
        </Card>

        <Card style={cardStyle}>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : rows.length === 0 ? (
              <div className="text-center py-5 text-muted">Không có phiếu thu nào</div>
            ) : (
              <>
                <div className="acc-table-wrap">
                <Table striped hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Mã phiếu</th>
                      <th>Ngày thu</th>
                      <th className="acc-col-hide-sm">Đối tượng thu</th>
                      <th className="text-end">Số tiền</th>
                      <th className="text-center acc-col-hide-sm">TK nhận</th>
                      <th className="acc-col-hide-sm">Line</th>
                      <th className="acc-col-hide-sm">Danh mục</th>
                      <th className="acc-col-hide-sm">Nội dung thu</th>
                      <th className="text-center">Trạng thái</th>
                      <th className="text-center">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => {
                      const evidencePaths = parseEvidencePaths(r.evidencePaths);
                      const isManual = r.receiptType !== "AUTO_RECONCILIATION";
                      const canEditRow = perm.canEdit && r.status === "DRAFT" && isManual;
                      const canCancelRow = perm.canEdit && (r.status === "DRAFT" || r.status === "APPROVED") && isManual;
                      return (
                      <tr key={r.id || idx}>
                        <td className="fw-semibold">{r.code || `#${r.id}`}</td>
                        <td>{r.receiptDate}</td>
                        <td className="acc-col-hide-sm">{r.payerCode || r.payerName || "—"}</td>
                        <td className="text-end fw-semibold">{fmtVND(r.amount)}</td>
                        <td className="text-center acc-col-hide-sm">{r.bankAccountName || "—"}</td>
                        <td className="acc-col-hide-sm">{r.lineName || "—"}</td>
                        <td className="small acc-col-hide-sm">{r.categoryDisplayLabel || formatCategoryLabel(r.receiptType, r.categoryName, true)}</td>
                        <td className="acc-col-hide-sm">{renderDescription(r.description)}</td>
                        <td className="text-center">
                          <Badge bg={STATUS_MAP[r.status]?.bg || "secondary"}>
                            {STATUS_MAP[r.status]?.label || r.status}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <div className="acc-action-btns">
                            {canEditRow && (
                              <Button variant="outline-primary" size="sm" title="Sửa" onClick={() => openEdit(r)}>
                                <FaPen />
                              </Button>
                            )}
                            {perm.canEdit && r.status === "DRAFT" && isManual && (
                              <Button variant="outline-info" size="sm" title="Duyệt" onClick={() => handleApprove(r.id)}>
                                <FaCheck />
                              </Button>
                            )}
                            {perm.canEdit && r.status === "APPROVED" && isManual && (
                              <Button variant="outline-success" size="sm" title="Xác nhận đã thu" onClick={() => handleConfirm(r.id, r.receiptType)}>
                                <FaCheck />
                              </Button>
                            )}
                            {perm.canEdit && r.status === "DRAFT" && !isManual && (
                              <Button variant="outline-success" size="sm" title="Xác nhận" onClick={() => handleConfirm(r.id, r.receiptType)}>
                                <FaCheck />
                              </Button>
                            )}
                            {canCancelRow && (
                              <Button variant="outline-danger" size="sm" title="Huỷ" onClick={() => handleCancel(r.id)}>
                                <FaBan />
                              </Button>
                            )}
                            {evidencePaths.length > 0 && (
                              <Button variant="outline-info" size="sm" title="Xem chứng từ" onClick={() => setEvidenceModal(evidencePaths)}>
                                <FaImage />
                              </Button>
                            )}
                            <Button variant="outline-secondary" size="sm" title="In" onClick={() => handlePrint(r.id)}>
                              <FaPrint />
                            </Button>
                            {r.status === "CONFIRMED" && (
                              <Button variant="outline-warning" size="sm" title="Xem công nợ" onClick={() => handleViewAllocations(r.id)}>
                                <FaLink />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </Table>
                </div>
                <div className="acc-table-footer">
                  <small className="text-muted">Tổng: {totalElements} phiếu</small>
                  <Pagination
                    hidePaginationWhenSinglePage
                    showItemsPerPage={false}
                    pageData={{ page: { number: page, totalPages, totalElements, size: PAGE_SIZE } }}
                    onPageChange={(p) => fetchData(p - 1)}
                  />
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={closeModal} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>{editId ? "Sửa phiếu thu" : "Tạo phiếu thu"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Đối tượng thu <span className="text-danger">*</span></Form.Label>
                    <Select
                      options={payerOptions}
                      value={selectedPayer}
                      onChange={(opt) => onFormChange("payerId", opt?.value ? String(opt.value) : "")}
                      placeholder="-- Chọn đối tượng thu --"
                      isSearchable
                      isClearable
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Line KD</Form.Label>
                    <Select
                      options={lineOptions}
                      value={selectedLines}
                      onChange={(opts) => onFormChange("lineIds", (opts || []).map((o) => o.value))}
                      placeholder="Chọn một hoặc nhiều line..."
                      isMulti
                      isClearable
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Danh mục thu <span className="text-danger">*</span></Form.Label>
                    <Form.Select value={form.categoryId} onChange={(e) => onFormChange("categoryId", e.target.value)}>
                      <option value="">-- Chọn danh mục --</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.code ? `${c.code} - ` : ""}{c.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Tài khoản nhận <span className="text-danger">*</span></Form.Label>
                    <Form.Select value={form.bankAccountId} onChange={(e) => onFormChange("bankAccountId", e.target.value)}>
                      <option value="">-- Chọn tài khoản --</option>
                      {bankAccounts.map((b) => (
                        <option key={b.id} value={b.id}>{b.bankName} - {b.accountNumber}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Số tiền <span className="text-danger">*</span></Form.Label>
                    <MoneyInput value={form.amount} onValueChange={(v) => onFormChange("amount", v)} placeholder="Nhập số tiền" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mô tả</Form.Label>
                    <Form.Control as="textarea" rows={1} value={form.description} onChange={(e) => onFormChange("description", e.target.value)} placeholder="Ghi chú..." />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal}>Đóng</Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? <Spinner animation="border" size="sm" /> : editId ? "Cập nhật" : "Tạo phiếu thu"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={!!allocationModal} onHide={() => setAllocationModal(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Phân bổ công nợ — Phiếu #{allocationModal?.id}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {(allocationModal?.rows || []).length === 0 ? (
              <div className="text-muted">Chưa có phân bổ công nợ</div>
            ) : (
              <Table striped size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Khoản CN</th>
                    <th className="text-end">Số tiền</th>
                    <th>Ngày</th>
                  </tr>
                </thead>
                <tbody>
                  {(allocationModal?.rows || []).map((a) => (
                    <tr key={a.id}>
                      <td>#{a.debtId}</td>
                      <td className="text-end">{fmtVND(a.amount)}</td>
                      <td>{a.createdAt || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
        </Modal>

        <Modal show={!!descriptionModal} onHide={() => setDescriptionModal(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Nội dung thu</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ whiteSpace: "pre-wrap" }}>{descriptionModal}</Modal.Body>
        </Modal>

        <Modal show={!!evidenceModal} onHide={() => setEvidenceModal(null)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Chứng từ thanh toán</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex flex-wrap gap-3">
              {(evidenceModal || []).map((path, idx) => (
                <a key={idx} href={resolveUploadUrl(path)} target="_blank" rel="noopener noreferrer">
                  <img src={resolveUploadUrl(path)} alt={`evidence-${idx}`} className="rounded border" style={{ maxWidth: 200, maxHeight: 200, objectFit: "cover" }} />
                </a>
              ))}
            </div>
          </Modal.Body>
        </Modal>
      </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default ReceiptVoucherList;
